import {
  getDecompositionTreeLayout,
  type DecompositionTreeLayout,
} from "#decomposition-tree-layout";
import {
  ResumableZoom,
  useTransformationState,
  type ResumableZoomRefType,
} from "@-/com.npmjs.react-native-zoom-toolkit";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import {
  EMPTY_DECOMPOSITION_MAP,
  useDecompositionMapQueryOptions,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.decomposition";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { getDecompositionTreeNodeLabel } from "#get-decomposition-tree-node-label";
import { createAnimatedComponent, useAnimatedProps } from "@-/com.npmjs.react-native-reanimated";
import { Svg, G, Line, Path, Rect, Text as SvgText } from "@-/com.npmjs.react-native-svg";
import {
  finiteSvgNumber,
  finiteSvgViewBox,
  svgDivisor,
} from "@-/dev.improve.haniverse.lib.svg-geometry";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import {
  useFontStyleForGrapheme,
  useGraphemeDisplayText,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.font";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type {
  ComponentButtonMorphemeSlice,
  GraphemeMorphemeMnemonicsPanelMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { useMorphemeMnemonicsPanelMorphemeSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { MorphemeMnemonicsMeaningViewContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._context";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { useContext, useMemo, useRef, useState } from "react";

const AnimatedSvg = createAnimatedComponent(Svg);
const LABEL_WORD_SEPARATOR = /\s+/;
const EMPTY_STROKE_GLYPHS: ReadonlySet<string> = new Set();

export function DecompositionTreeNode({
  card,
  onSelect,
}: {
  card: GraphemeMorphemeMnemonicsPanelMorphemeSlice;
  onSelect: () => void;
}) {
  const { onUpdate, state } = useTransformationState("common");
  const zoomRef = useRef<ResumableZoomRefType>(null);
  const [viewport, setViewport] = useState({ height: 0, width: 0 });
  const { data: decompositionMap = EMPTY_DECOMPOSITION_MAP } = useQuery(
    useDecompositionMapQueryOptions(),
  );
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (context) => context.db);
  const { data: strokeGlyphs = EMPTY_STROKE_GLYPHS } = useQuery({
    queryKey: ["decompositionTreeStrokeGlyphs", { staticDb }],
    queryFn: async () => {
      const rows = await staticDb(import.meta.location).query.grapheme.findMany({
        columns: { glyph: true },
        where: { strokeCount: 1 },
      });
      return new Set(rows.map((row) => row.glyph));
    },
  });
  const tree = useMemo(
    () => getDecompositionTreeLayout(card.grapheme, decompositionMap, strokeGlyphs),
    [card.grapheme, decompositionMap, strokeGlyphs],
  );
  // Preserve compact cards for small trees; shrink large trees to show every branch.
  const fitScale = Math.min(
    1,
    Math.max(1, viewport.width - 24) / svgDivisor(tree.width),
    Math.max(1, viewport.height - 24) / svgDivisor(tree.height),
  );
  // One sanitized scale for both the animated viewBox and the touch-target layer below, so the
  // two can never disagree about where a node is.
  const safeFitScale = svgDivisor(fitScale);
  const animatedSvgProps = useAnimatedProps(() => {
    // `state.scale` is whatever the gesture library last wrote, and it passes through 0 during a
    // pinch reset. Dividing by it unguarded yields an infinite viewBox, which RNSVG turns into a
    // NaN layer frame and `-[CALayer setPosition:]` aborts the process on. See
    // `@-/dev.improve.haniverse.lib.svg-geometry`.
    const scale = svgDivisor(safeFitScale * finiteSvgNumber(state.scale.value, 1));
    // A zero-sized initial viewBox produces an invalid native SVG transform before layout.
    const width = Math.max(1, viewport.width) / scale;
    const height = Math.max(1, viewport.height) / scale;
    const x =
      tree.width / 2 - (viewport.width / 2 + finiteSvgNumber(state.translateX.value)) / scale;
    const y =
      tree.height / 2 - (viewport.height / 2 + finiteSvgNumber(state.translateY.value)) / scale;
    // Native SVG receives viewBox's extracted fields; web receives the SVG attribute.
    // The viewport stays at screen resolution, so paths and type redraw at every zoom.
    const box = finiteSvgViewBox(x, y, width, height);
    return {
      minX: box.minX,
      minY: box.minY,
      vbHeight: box.height,
      vbWidth: box.width,
      viewBox: box.viewBox,
    };
  });
  const ready = viewport.width > 0 && viewport.height > 0;

  return (
    <View className="flex-1 gap-3">
      <View
        className="flex-1 overflow-hidden rounded-xs"
        onLayout={({ nativeEvent: { layout } }) =>
          setViewport({ height: layout.height, width: layout.width })
        }
        testID="decomposition-tree-viewport"
      >
        {ready ? (
          <>
            <View className="absolute inset-0" pointerEvents="none">
              <AnimatedSvg
                accessible={false}
                animatedProps={animatedSvgProps}
                height={viewport.height}
                width={viewport.width}
              >
                <DecompositionTreeBranch
                  mode="drawing"
                  onSelect={onSelect}
                  rootCard={card}
                  tree={tree}
                />
              </AnimatedSvg>
            </View>
            <ResumableZoom
              extendGestures
              key={`${card.grapheme}-${viewport.width}-${viewport.height}`}
              // Node Pressables own taps and long presses. The canvas owns only pan/pinch.
              longPressDuration={10_000}
              maxScale={Math.max(4, 2 / safeFitScale)}
              onUpdate={onUpdate}
              panMode="clamp"
              ref={zoomRef}
              scaleMode="clamp"
              tapsEnabled={false}
            >
              <View
                style={{
                  // Blank canvas around the fitted tree keeps pan bounds nonzero at scale 1.
                  height: viewport.height * 2 + finiteSvgNumber(tree.height * safeFitScale, 1),
                  width: viewport.width * 2 + finiteSvgNumber(tree.width * safeFitScale, 1),
                }}
              >
                <View
                  className="absolute items-center"
                  style={{
                    height: finiteSvgNumber(tree.height, 1),
                    left: finiteSvgNumber(
                      viewport.width + (tree.width * safeFitScale - tree.width) / 2,
                    ),
                    top: finiteSvgNumber(
                      viewport.height + (tree.height * safeFitScale - tree.height) / 2,
                    ),
                    transform: [{ scale: safeFitScale }],
                    width: finiteSvgNumber(tree.width, 1),
                  }}
                >
                  <DecompositionTreeBranch
                    mode="targets"
                    onSelect={onSelect}
                    rootCard={card}
                    tree={tree}
                  />
                </View>
              </View>
            </ResumableZoom>
          </>
        ) : null}
      </View>
      <View className="flex-row items-center justify-between gap-3">
        <Text className="shrink text-xs text-muted-foreground">
          {tree.children.length > 0
            ? "Drag to move · Pinch to zoom"
            : "No further components · Pinch to zoom"}
        </Text>
        <Pressable
          accessibilityLabel="Fit decomposition tree to screen"
          accessibilityRole="button"
          className="min-h-11 justify-center rounded-full border border-border bg-muted/70 px-4 active:bg-accent"
          onPress={() => zoomRef.current?.reset()}
        >
          <Text className="text-xs font-medium text-foreground">Fit</Text>
        </Pressable>
      </View>
    </View>
  );
}

function DecompositionTreeBranch({
  component,
  mode,
  onSelect,
  rootCard,
  tree,
}: {
  component?: ComponentButtonMorphemeSlice;
  mode: "drawing" | "targets";
  onSelect: () => void;
  rootCard?: GraphemeMorphemeMnemonicsPanelMorphemeSlice;
  tree: DecompositionTreeLayout;
}) {
  const { children, grapheme, nodeSize } = tree;
  const isStroke = nodeSize === 40;
  const queryOptions = useMorphemeMnemonicsPanelMorphemeSliceQueryOptions(grapheme);
  const { data, isPending } = useQuery({
    ...queryOptions,
    enabled: rootCard === undefined,
    initialData: rootCard,
  });
  const card = rootCard ?? data;
  const { morphemeHrefObject } = useContext(MorphemeMnemonicsMeaningViewContext);
  const cardPageNavigator = useMorphemePanelsHrefNavigator();
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const glyph = getGraphemeGlyph(grapheme);
  const fontStyle = useFontStyleForGrapheme(glyph.display);
  const displayText = useGraphemeDisplayText(glyph.display);
  const dark = useTheme() === "dark";
  const selectionItems = useMemo(() => [morphemeSelectionItem(grapheme)], [grapheme]);
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const prevalentPinyin = usePrevalentPinyin(grapheme, card?.pinyinList ?? []);
  const pinyin = prevalentPinyin[0]
    ? toDiacriticPinyin(prevalentPinyin[0].toneNumbersPinyin)
    : null;
  const strokes = card?.graphemeStrokes ?? component?.graphemeStrokes;
  const label =
    component?.componentMnemonic ??
    (card
      ? getDecompositionTreeNodeLabel(card)
      : (component?.back ?? component?.pinyin.gloss ?? (isPending ? "…" : grapheme)));
  const isMnemonic =
    component?.componentMnemonic !== undefined ||
    card?.mnemonicTerms.some((term) => term.trim() !== "");

  const foreground = dark ? "#f5f5f5" : "#171717";
  const muted = dark ? "#a3a3a3" : "#737373";
  const border = dark ? "#404040" : "#dedede";
  // Every coordinate below lands in a native layer frame, where a non-finite value aborts the
  // process rather than throwing. The layout currently guarantees finite widths; these guards keep
  // that a local property of this component rather than a cross-module invariant.
  const treeWidth = finiteSvgNumber(tree.width, 96);
  const cardX = treeWidth / 2 - nodeSize / 2;
  const labelLines = wrapDecompositionLabel(label);
  const childrenWithOffsets = children.map((child, index) => ({
    child,
    width: finiteSvgNumber(child.width, 96),
    x: children
      .slice(0, index)
      .reduce((width, sibling) => width + finiteSvgNumber(sibling.width, 96), 0),
  }));

  if (mode === "drawing") {
    return (
      <G>
        <G transform={`translate(${cardX},0)`}>
          <Rect
            fill={isSelected ? (dark ? "#263a35" : "#e1eee8") : dark ? "#252525" : "#f9f7f3"}
            height={nodeSize}
            rx={2}
            stroke={border}
            strokeWidth={1}
            width={nodeSize}
          />
          {strokes && strokes.length > 0 ? (
            <G
              transform={`translate(${isStroke ? 6 : pinyin ? 12 : 26},${isStroke ? 6 : 7}) scale(0.02734375,-0.02734375) translate(0,-900)`}
            >
              {strokes.map((stroke, index) => (
                <Path d={stroke.path} fill={foreground} key={index} />
              ))}
            </G>
          ) : (
            <SvgText
              fill={foreground}
              fontFamily={fontStyle?.fontFamily}
              fontSize={26}
              textAnchor="middle"
              x={isStroke ? 20 : pinyin ? 26 : 40}
              y={32}
            >
              {displayText}
            </SvgText>
          )}
          {!isStroke && pinyin ? (
            <SvgText fill={muted} fontSize={10} x={43} y={27}>
              {pinyin}
            </SvgText>
          ) : null}
          {!isStroke ? <Line stroke={border} x1={10} x2={70} y1={43} y2={43} /> : null}
          {!isStroke &&
            labelLines.map((line, index) => (
              <SvgText
                fill={foreground}
                fontSize={11}
                fontStyle={isMnemonic ? "italic" : "normal"}
                key={index}
                textAnchor="middle"
                x={40}
                y={labelLines.length > 1 ? 57 + index * 14 : 64}
              >
                {line}
              </SvgText>
            ))}
        </G>
        {children.length > 0 ? (
          <Line
            stroke={muted}
            strokeOpacity={0.45}
            x1={treeWidth / 2}
            x2={treeWidth / 2}
            y1={nodeSize}
            y2={nodeSize + 16}
          />
        ) : null}
        {childrenWithOffsets.map(({ child, width, x }, index) => (
          <G key={`${child.grapheme}-${index}`}>
            <Path
              d={`M ${index === 0 ? x + width / 2 : x} ${nodeSize + 16} H ${index === children.length - 1 ? x + width / 2 : x + width} M ${x + width / 2} ${nodeSize + 16} V ${nodeSize + 32}`}
              fill="none"
              stroke={muted}
              strokeOpacity={0.45}
            />
            <G transform={`translate(${x},${nodeSize + 32})`}>
              <DecompositionTreeBranch
                component={card?.components.find(
                  (part) => part.graphemes?.[0]?.glyph === child.grapheme,
                )}
                mode="drawing"
                onSelect={onSelect}
                tree={child}
              />
            </G>
          </G>
        ))}
      </G>
    );
  }

  // Only transparent, accessible touch targets are transformed by the gesture library.
  // All visible content is rendered by the separate, unscaled SVG viewport above.
  return (
    <View className="items-center" style={{ width: treeWidth }}>
      <LongPressable
        accessibilityLabel={`${grapheme}, ${pinyin ?? ""}, ${label}, decomposition tree ${rootCard ? "item" : "component"}`}
        accessibilityRole="button"
        accessibilityState={{ selected: isSelected }}
        className={isStroke ? "size-10" : "size-20"}
        hitSlop={4}
        onLongPress={onLongPress}
        onPress={() => {
          if (consumePress()) {
            return;
          }
          onSelect();
          cardPageNavigator.push.mutate({ morpheme: grapheme, prefix: morphemeHrefObject });
        }}
      />
      {children.length > 0 ? (
        <View className="flex-row items-start pt-8">
          {children.map((child, index) => (
            <DecompositionTreeBranch
              component={card?.components.find(
                (part) => part.graphemes?.[0]?.glyph === child.grapheme,
              )}
              key={`${child.grapheme}-${index}`}
              mode="targets"
              onSelect={onSelect}
              tree={child}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}

/** Two compact lines; the full meaning remains available to assistive technology. */
function wrapDecompositionLabel(label: string): string[] {
  const words = label.trim().split(LABEL_WORD_SEPARATOR);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    if (line && (line + " " + word).length > 11) {
      lines.push(line);
      line = word;
    } else {
      line = line ? line + " " + word : word;
    }
  }
  if (line) {
    lines.push(line);
  }
  return lines
    .slice(0, 2)
    .map((text, index) =>
      text.length > 11 || (index === 1 && lines.length > 2)
        ? text.slice(0, 10).trimEnd() + "…"
        : text,
    );
}
